import type { MouseEvent } from "react";

import { DotsHorizontalIcon, PopoverMenu, Typo } from "@partners/ui";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";

import type { NavLinkData } from "./AuthenticatedBaseHeader";

import { useHeaderTranslate } from "./useHeaderTranslate";

type NavOverflowMenuProps = {
  items: NavLinkData[];
  isNavLinkSelected: (linkPath: string) => boolean;
  onNavigate: (
    event: MouseEvent<HTMLAnchorElement>,
    segmentEvent: string,
  ) => void;
};

export const navOverflowTriggerClassName =
  "flex items-center justify-center rounded-lg p-2 text-white hover:bg-[color:var(--ps-color-cta-secondary-base--dark)]";

export const NavOverflowIcon = () => <DotsHorizontalIcon className="h-5 w-5" />;

export const NavOverflowMenu = ({
  items,
  isNavLinkSelected,
  onNavigate,
}: NavOverflowMenuProps) => {
  const { moreNavItems } = useHeaderTranslate();
  const hasSelectedItem = items.some(({ to }) => isNavLinkSelected(to));

  return (
    <PopoverMenu
      openOn="hover"
      align="start"
      contentClassName="min-w-[180px] p-4"
      asChild
      content={(close) => (
        <ul data-testid="nav-overflow-menu">
          {items.map(({ to, label, segmentEvent }) => (
            <li key={to}>
              <Link
                to={to}
                className={clsx(
                  "block rounded-lg p-2 hover:bg-surface-tonal-default",
                  isNavLinkSelected(to) && "bg-surface-tonal-default",
                )}
                onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                  if (segmentEvent) onNavigate(event, segmentEvent);
                  close();
                }}
              >
                <Typo
                  element="div"
                  type="labelMD"
                  textColor="text-text-primary"
                >
                  {label}
                </Typo>
              </Link>
            </li>
          ))}
        </ul>
      )}
    >
      <button
        aria-label={moreNavItems}
        className={clsx(
          navOverflowTriggerClassName,
          hasSelectedItem && "bg-cta-secondary-hover--dark",
        )}
      >
        <NavOverflowIcon />
      </button>
    </PopoverMenu>
  );
};
